import { createSelector } from 'reselect';
import _uniq from 'lodash/uniq';
import _intersection from 'lodash/intersection';
import { AppEnvs, ContentType, AuthTypes, AdobePass, ComScoreC3, Entitlements } from 'enums';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import appSelector, {
    editionSelector,
    isDomesticSelector,
    isIntlSelector,
    intlAppSelector,
    isSecureSelector,
    playbackMetaSelector
} from 'common/selectors/appSelector';
import providerSelector from 'common/selectors/providerSelector';
import { pageAPIParamsSelector } from 'common/selectors/apiParamsSelector';
import {
    adobePassInfoSelector,
    adobePassMetadataSelector,
    ipInfoSelector,
    userAuthSelector,
    subscriptionsSelector,
    userTrackingSelector,
    superBundleSelector
} from 'common/selectors/authenticationSelectors';
import pageSelector, { showPromoSelector } from 'common/selectors/pageSelector';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import selectedStreamSelector from 'common/selectors/selectedStreamSelector';
import fanSelector from 'common/selectors/fanSelector';
import { oneIdLoggedInSelector, oneIdEntitlementsSelector } from 'common/selectors/oneIdSelector';
import routingSelector from 'common/selectors/routingSelector';
import { getAccessMethodFromAuthTypes, streamHasAuthType } from 'lib/auth';
import { isEmpty } from 'lib/util';
import {
    OMNITURE_CONSTANTS,
    getVideoTypeFromStream,
    getOmnitureUserTrackingData
} from 'lib/omniture';
import cookie from 'cookie-cutter';

const { METADATA: { UPSTREAM_USERID } } = AdobePass;

// utility functions to format data specifically for nielsen
function yesOrNo(boolParam) {
    return (boolParam === true ? 'yes' : 'no');
}

/**
 * getNielsenValue - Returns the expected nielsen value. If no value is defined, the fallbackValue
 * will be returned. Ensures backwards compatibility when called without fallbackValue will result
 * as 'Not Applicable'
 *
 * @param  {string} value - the expected value to be returned if defined
 * @param  {string} fallbackValue = 'Not Applicable' - fallback value when value is not defined
 * @return {string}
 */
export const getNielsenValue = (value, fallbackValue = 'Not Applicable') => (
    (value && typeof value === 'string' && value.trim() !== '') ? value : fallbackValue
);

/**
 * getEventValueFromContent - Returns the proper NielsenValue if the content.type matches the
 * contentType parameter. Provides a fallback value option.
 *
 * @param  {object} content - object containing the type property that is used to compare with contentType
 * @param  {string} contentType - content type to match current content with
 * @param  {string} value - Expected value to be returned if condition is truthy
 * @param  {string} fallbackValue = 'Not Applicable' - fallback value when value is not defined
 * @return {string}
 */
export const getEventValueFromContent = (content = {}, contentType, value, fallbackValue = 'Not Applicable') => (
    (content && content.type === contentType ? getNielsenValue(value, fallbackValue) : fallbackValue)
);

function getNielsenPersonalizationData(fan) {
    if (Object.keys(fan).length > 0) {
        const { profile: { settings = {} } } = fan;
        const { activeProducts = {} } = settings;
        const { metaData = {} } = activeProducts;

        const hasFantasy = Object.keys(metaData).filter((key) => (metaData[key] === 'true'))
                .map((key) => (key.indexOf('lm_') !== -1)).length > 0;

        const hasFavorites = fan.preferences && (fan.preferences.length > 0);
        const hasNotifications = fan.profile && fan.profile.hasNotificationPreferences;
        const videoAutoStart = settings.videoAutoStart && (settings.videoAutoStart.value === '2');
        return `has favorites:${yesOrNo(hasFavorites)}|fantasy:${yesOrNo(hasFantasy)}|notifications:${hasNotifications}|docking:no-autostart:${yesOrNo(videoAutoStart)}`; // eslint-disable-line
    }
    return 'Not Applicable';
}

function getNielsenLoginStatusFromFan(fan) {
    if (Object.keys(fan).length > 0) {
        return 'Registered:Logged in Active';
    }
    return 'Not Applicable';
}

function getActiveSubscriptions(subscriptions = [], stream = {}) { // eslint-disable-line
    const { packages } = stream;
    const activeSubscriptions = subscriptions
        .filter(({ isActive, products: [product = {}] = [], status: { type } }) => {
            const { productEntitlements: [entitlement = {}] = [] } = product;
            return isActive && type === 'SUBSCRIBED' && (!packages || packages.indexOf(entitlement.name) > -1);
        });

    return activeSubscriptions;
}

function formatNielsenData(
    playbackMeta = {},
    fan = {},
    page = {},
    provider = {},
    selectedContent = {},
    selectedStream = {},
    userAuth = [],
    entitlementsTracking,
    adobePassMetadata = {},
    constants,
    navmethod,
    storyId,
    wasPersonalized
) {
    const { Site, ReportSuite, Edition } = constants;
    const { playbackState = {}, tracking = {} } = playbackMeta;
    let { [UPSTREAM_USERID]: usid } = adobePassMetadata;
    if (provider.abbreviation === 'Comcast_SSO' || provider.abbreviation === 'YouTubeTV') {
        usid = undefined;
    }
    const Language = (page.lang || '').toLowerCase();
    const Placement = selectedContent.placement || 'player-direct';
    const PlayLocation = navmethod;
    const InsiderStatus = yesOrNo(fan.isInsider);

    // watch specific values
    const networkVal = (
        (selectedStream.source && selectedStream.source.dtc && selectedStream.source.dtc.toLowerCase()) ||
        (selectedStream.source && selectedStream.source.id && selectedStream.source.id.toLowerCase()) ||
        (selectedStream.source && selectedStream.source.name && selectedStream.source.name.toLowerCase())
    );
    const Network = getEventValueFromContent(selectedContent, ContentType.LISTING, networkVal);
    const AirDate = getEventValueFromContent(selectedContent, ContentType.LISTING, selectedContent.date);
    const AirTime = getEventValueFromContent(selectedContent, ContentType.LISTING, selectedContent.time);
    // TODO: DTC - `airingId` not available in stream
    const AiringID = getEventValueFromContent(selectedContent, ContentType.LISTING, playbackState.airingId || playbackState.id);
    const isStudio = selectedContent.type === ContentType.LISTING ? yesOrNo(tracking.isStudio) : 'Not Applicable';
    const trackingId = tracking.trackingId;
    const WasPersonalized = yesOrNo(wasPersonalized);
    const ProgramCode = getEventValueFromContent(selectedContent, ContentType.LISTING, tracking.programCode);
    const SegC = ProgramCode;
    const SegA = selectedStream.name;

    const AccessMethod = getEventValueFromContent(
        selectedContent,
        ContentType.LISTING,
        getAccessMethodFromAuthTypes(userAuth),
        'Not Authenticated'
    );
    const AuthenticationStatus = AccessMethod;
    const AffiliateName = getEventValueFromContent(
        selectedContent,
        ContentType.LISTING,
        provider.package ? provider.abbreviation : provider.name,
        'Not Authenticated'
    );
    const AffiliateID = getEventValueFromContent(
        selectedContent,
        ContentType.LISTING,
        provider.package ? provider.package.name : provider.abbreviation,
        'Not Authenticated'
    );
    usid = getEventValueFromContent(selectedContent, ContentType.LISTING, usid);

    const LoginStatus = getNielsenLoginStatusFromFan(fan);
    const Personalization = getNielsenPersonalizationData(fan);
    const Embedded = 'no';

    const SWID = fan.id || (typeof document !== 'undefined' && cookie.get('SWID'));
    const unid = fan.id || (typeof document !== 'undefined' && cookie.get('UNID'));
    const UserAgent = (typeof window !== 'undefined') ? window.navigator.userAgent : 'Not Applicable';

    // Entitlements
    const { purchaseMethod, subscriberType, entitlements, oneIdLoggedIn, userTracking, hasSuperBundle } = entitlementsTracking;
    const PurchaseMethod = purchaseMethod || 'Not Applicable';
    const SubscriberType = subscriberType || 'Not Applicable';
    const DisneyPlusBundle = hasSuperBundle ? 'Yes' : 'No';
    const DTCEntitlements = entitlements || 'Not Applicable';
    const DSSID = getOmnitureUserTrackingData(oneIdLoggedIn, userTracking);

    // #TODO get real orientation when mobile player launches
    const Orientation = 'Desktop';
    const VideoType = getVideoTypeFromStream(selectedContent);
    const ContentDuration = tracking.duration || -1;
    const Sport = getNielsenValue(tracking.sport);
    let League = getNielsenValue(tracking.league);
    // if No League, set it equal to the sport, ask Hopkins.
    League = (League !== 'No League' ? League : Sport);
    // TODO: DTC - is this currently working?
    const GameID = getNielsenValue(selectedContent.gameId);
    let PlayerName = (VideoType === ContentType.VOD) ? 'Brightcove-vod' : 'Brightcove-live';
    if (selectedStream.authTypes && selectedStream.authTypes.indexOf(AuthTypes.BAM) > -1) {
        PlayerName = 'BAM-live';
    }

    const VideoTypeDetail = getNielsenValue(tracking.coverageType);
    // #TODO: get these values
    const isVOD = selectedContent.type === ContentType.VOD;
    const dateTimeArray = isVOD ? (tracking.originalPublishDate || '').split('T') : [];
    const PublishDate = isVOD ? dateTimeArray[0] : 'Not Applicable';
    const PublishTime = isVOD ? dateTimeArray[1] : 'Not Applicable';
    const ExpirationDate = isVOD ? tracking.expirationDate : 'Not Applicable';
    const DateLastModified = isVOD ? tracking.lastModified : 'Not Applicable';
    const CMSID = isVOD ? `${selectedContent.id}-${tracking.name}` : 'Not Applicable';
    const ShowCode = isVOD ? tracking.showCode : 'Not Applicable';
    const VideoPlaylist = 'Not Applicable';
    const isfullepisode = isVOD ? 'n' : 'y';
    const StoryID = storyId;

    return {
        Site,
        ReportSuite,
        SWID,
        UserAgent,
        Language,
        Placement,
        PlayLocation,
        Network,
        InsiderStatus,
        AirDate,
        AirTime,
        AiringID,
        isStudio,
        trackingId,
        WasPersonalized,
        SegA,
        SegC,
        ProgramCode,
        LoginStatus,
        Personalization,
        Embedded,
        Orientation,
        AccessMethod,
        AffiliateID,
        AffiliateName,
        AuthenticationStatus,
        PurchaseMethod,
        SubscriberType,
        Entitlements: DTCEntitlements,
        Edition,
        GameID,
        StoryID,
        VideoType,
        ContentDuration,
        Sport,
        League,
        PlayerName,
        VideoTypeDetail,
        PublishDate,
        PublishTime,
        ExpirationDate,
        DateLastModified,
        CMSID,
        ShowCode,
        VideoPlaylist,
        isfullepisode,
        unid,
        usid,
        navmethod,
        DSSID,
        DisneyPlusBundle
    };
}

const analyticsSelector = ({ app: { analytics = null } = {} }) => analytics;

export const analyticsConstantsSelector = createSelector(
    appSelector,
    isSecureSelector,
    isIntlSelector,
    intlAppSelector,
    editionSelector,
    ({ env, location: { path = '' } }, isSecure, isIntl, intlApp, { edition }) => {
        const {
            Channel,
            Site,
            ReportSuite,
            ReportSuiteQA,
            ReportSuiteIntl,
            ReportSuiteApp,
            TrackingServer,
            SecureTrackingServer
        } = OMNITURE_CONSTANTS;
        const constants = {
            Channel,
            Site,
            ReportSuite,
            TrackingServer: isSecure ? SecureTrackingServer : TrackingServer,
            Edition: edition === 'us' ? 'en-us' : edition
        };
        if (intlApp) {
            constants.ReportSuite = ReportSuiteApp;
        } else if (isIntl) { // TODO: remove this on the 1st of the following month of the last intl. launch
            constants.ReportSuite = ReportSuiteIntl;
        } else if (env !== AppEnvs.prod) {
            constants.ReportSuite = ReportSuiteQA;
        }

        if (path.indexOf('/activate') !== -1) {
            constants.Channel = 'device:activation';
        }

        return constants;
    }
);

export const storyIdSelector = createSelector(
    routingSelector,
    pageSelector,
    ({ locationBeforeTransitions }) => {
        const { query = {} } = locationBeforeTransitions || {};
        const storyId = query['om-storyid'] || 'Not Applicable';

        return storyId;
    }
);

export const navmethodSelector = createSelector(
    routingSelector,
    selectedContentSelector,
    pageSelector,
    ({ locationBeforeTransitions }, selectedContent, page) => {
        const { isHero, placement, bucketName, pageId, pageName, 'om-navmethod': omNavmethod } = selectedContent || {};
        const { query = {} } = locationBeforeTransitions || {};
        let navmethod = query['om-navmethod'] || omNavmethod;
        // set navmethod on tile click, based on position
        if (!isEmpty(selectedContent) && !navmethod) {
            const _pageId = pageId || page.id;
            const _pageName = pageName || page.name;
            let path = 'watch';
            if (_pageId === 'collection') {
                path += `:${_pageName}`;
            } else if (_pageId !== 'home') {
                path += `:${_pageId}`;
            }

            let rowName = 'Not Applicable';
            if (isHero || placement === 'inline-hero') {
                rowName = 'hero';
            } else if (bucketName) {
                rowName = bucketName;
            }

            navmethod = `${path}:${rowName}`;
        }

        return navmethod;
    }
);

export const entitlementsTrackingSelector = createSelector(
    isDomesticSelector,
    subscriptionsSelector,
    oneIdEntitlementsSelector,
    oneIdLoggedInSelector,
    userTrackingSelector,
    superBundleSelector,
    (isDomestic, subscriptions, entitlementsArg = [], oneIdLoggedIn, userTracking, hasSuperBundle) => {
        if (!isDomestic) {
            return {};
        }

        let purchaseMethod = [];
        let subscriberType = 'not subscribed';
        let entitlements = entitlementsArg;

        if (subscriptions) {
            purchaseMethod = _uniq(subscriptions
                .map(({ source: { provider } = {} }) => provider && provider.toLowerCase()));

            const subscriberTypes = _uniq(subscriptions
                .map(({ isActive }) => isActive));

            if (subscriberTypes.length) {
                subscriberType = (subscriberTypes.some(isActive => !!isActive)) ? 'active' : 'inactive';
            }
        }

        if (!purchaseMethod.length) {
            purchaseMethod = ['no entitlements'];
        }

        if (!entitlements.length) {
            entitlements = ['no entitlements'];
        }

        return {
            hasSuperBundle,
            subscriberType,
            oneIdLoggedIn,
            userTracking,
            purchaseMethod: purchaseMethod.join(','),
            entitlements: entitlements.join(',')
        };
    }
);

export const promoTrackingSelector = createSelector(
    bootstrapSelector,
    pageSelector,
    showPromoSelector,
    ({ sku = [] }, { id }, showPromo) => {
        let productsVal;
        if (id !== 'espnplus' || !showPromo) {
            return productsVal;
        }
        const products = sku.filter(({ name = '' }) => name.toLowerCase().indexOf('base') > -1)
            .map(({ value }) => `;${value}`);
        productsVal = `D2C${products.join(',')}`;
        return productsVal;
    }
);

export const wasPersonalizedSelector = createSelector(
    bootstrapSelector,
    pageAPIParamsSelector,
    ({ pageApi: { requireClientRenderParams = [] } = {} }, pageAPIParams) => {
        const hasPersonalizedParams = _intersection(Object.keys(pageAPIParams), requireClientRenderParams).length > 0;
        return hasPersonalizedParams;
    }
);

// last argument on the selector gets passed the previous arguments
export const nielsenTrackingSelector = createSelector(
    playbackMetaSelector,
    fanSelector,
    pageSelector,
    providerSelector,
    selectedContentSelector,
    selectedStreamSelector,
    userAuthSelector,
    entitlementsTrackingSelector,
    adobePassMetadataSelector,
    analyticsConstantsSelector,
    navmethodSelector,
    storyIdSelector,
    wasPersonalizedSelector,
    formatNielsenData
);

export const comscoreTrackingSelector = createSelector(
    editionSelector,
    selectedStreamSelector,
    ({ edition }, selectedStream) => {
        let c3;
        let c4 = edition === 'brazil' ? '"watchespncombr"' : '"watchespncom"';
        if (streamHasAuthType(selectedStream, AuthTypes.BAM)) {
            c4 = `"${ComScoreC3[Entitlements.ESPN_PLUS]}"`;
            const { packages = [] } = selectedStream;
            if (packages.length) {
                if (packages.indexOf(Entitlements.ESPN_PLUS) > -1) {
                    c3 = ComScoreC3[Entitlements.ESPN_PLUS];
                } else {
                    c3 = ComScoreC3[packages[0]];
                }
            }
        }
        const labels = {
            c4
        };
        if (c3) {
            labels.c3 = `"${c3}"`;
        }
        return {
            labels
        };
    }
);

export const pageTrackingSelector = createSelector(
    isDomesticSelector,
    adobePassInfoSelector,
    analyticsSelector,
    appSelector,
    ipInfoSelector,
    pageSelector,
    userAuthSelector,
    subscriptionsSelector,
    oneIdLoggedInSelector,
    entitlementsTrackingSelector,
    promoTrackingSelector,
    adobePassMetadataSelector,
    (
        isDomestic,
        adobePassInfo,
        analytics,
        app,
        ipInfo,
        page,
        userAuth,
        subscriptions,
        oneIdLoggedIn,
        entitlementsTracking,
        promoTracking,
        adobePassMetadata
    ) => ({
        isDomestic,
        adobePassInfo,
        analytics,
        app,
        ipInfo,
        page,
        userAuth,
        subscriptions,
        oneIdLoggedIn,
        entitlementsTracking,
        promoTracking,
        adobePassMetadata
    })
);

export const placementPrefixSelector = createSelector(
    pageSelector,
    (page) => {
        let prefix = page.id;
        if (page.id === 'collection' || page.id === 'product') {
            prefix += `-${page.name}`;
        }
        prefix += '-';
        return prefix;
    }
);



// WEBPACK FOOTER //
// ./app/common/selectors/analyticsSelectors.js